import React, { useRef, useState } from 'react'
import styled from '@emotion/styled'

import useOnClickOutside from '../../hooks/useOnClickOutside'
import { Stack, StackProps } from '../Stack/Stack'
import { Text } from '../Text/Text'
import { FieldLabel, FieldLabelProps } from '../FieldLabel/FieldLabel'
import { FieldMessage } from '../FieldMessage/FieldMessage'

export interface Suggestion {
  text: string
  value?: any
}

export interface AutosuggestProps extends Partial<FieldLabelProps> {
  suggestions: Required<Suggestion>[]
  value?: Suggestion
  onChange: (suggestion: Suggestion) => void
  onLeave?: () => void
  placeholder?: string
  attachRight?: boolean
  attachLeft?: boolean
  message?: string
  tone?: 'neutral' | 'positive' | 'negative'
  big?: boolean
  disabled?: boolean
  background?: string
}

export const Autosuggest: React.FC<AutosuggestProps> = ({
  suggestions,
  value,
  onChange,
  onLeave,
  placeholder,
  label,
  secondary,
  tertiary,
  tertiaryIcon,
  tertiaryAction,
  description,
  message,
  tone,
  big = false,
  disabled = false,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const dropdownRef = useRef(null)
  useOnClickOutside(dropdownRef, () => {
    setIsOpen(false)
    if (onLeave) onLeave()
  })

  const inputRef = useRef<HTMLInputElement>(null)

  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) setIsOpen(true)
    switch (event.key) {
      case 'Tab':
        setIsOpen(false)
        if (onLeave) onLeave()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (highlightedIndex !== null && highlightedIndex < suggestions.length - 1) {
          setHighlightedIndex(highlightedIndex + 1)
        } else {
          setHighlightedIndex(0)
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (highlightedIndex !== null && highlightedIndex > 0) {
          setHighlightedIndex(highlightedIndex - 1)
        } else {
          setHighlightedIndex(suggestions.length - 1)
        }
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex !== null) {
          onChange(suggestions[highlightedIndex])
          setIsOpen(false)
        }
        break
      case 'Escape':
        event.preventDefault()
        onChange({ text: '' })
        setIsOpen(false)
        setHighlightedIndex(null)
    }
  }

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ text: event.target.value })
    if (!value?.text) {
      setHighlightedIndex(0)
    }
  }

  const onClickAutosuggest = () => {
    setIsOpen(true)
    inputRef.current?.focus()
  }

  const onClickSuggestion = (suggestion: Suggestion) => {
    onChange(suggestion)
    setIsOpen(false)
  }

  const background = disabled
    ? 'var(--textfield-bg--disabled)'
    : props.background
    ? props.background
    : 'transparent'

  return (
    <Stack space="xs">
      {label && (
        <FieldLabel
          label={label}
          secondary={secondary}
          tertiary={tertiary}
          tertiaryIcon={tertiaryIcon}
          tertiaryAction={tertiaryAction}
          description={description}
        />
      )}

      <Wrapper
        ref={dropdownRef}
        onClick={onClickAutosuggest}
        big={big}
        {...props}
        background={background}
      >
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={value?.text}
          onChange={onInputChange}
          onKeyDown={onKeyDown}
          big={big}
          background={background}
          disabled={disabled}
          autoComplete="off"
        />

        {isOpen && !disabled && suggestions.length > 0 && (
          <SuggestionsStack
            className="suggestions-stack"
            onClick={(event: React.MouseEvent) => event.stopPropagation()}
            big={big}
          >
            {suggestions.map((suggestion, i) => (
              <SuggestionDiv
                key={i}
                highlighted={highlightedIndex === i}
                divider={i > 0}
                onClick={() => onClickSuggestion(suggestion)}
                onMouseEnter={() => setHighlightedIndex(i)}
              >
                <Text>{suggestion.text}</Text>
              </SuggestionDiv>
            ))}
          </SuggestionsStack>
        )}
      </Wrapper>

      {message && <FieldMessage tone={tone === 'neutral' ? 'light' : tone}>{message}</FieldMessage>}
    </Stack>
  )
}

const Wrapper = styled.div<Partial<AutosuggestProps>>`
  display: flex;
  position: relative;
  cursor: text;
  border: 1px solid var(--colors-subtle);
  border-radius: var(--border-radius--xxs);
  padding: 0 12px;

  ${(p) => `height: ${p.big ? '47px' : 'var(--space--xl)'};`}

  ${(p) =>
    p.attachLeft &&
    `
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
    
    .suggestions-stack {
      border-top-left-radius: 0;
    }
  `}

  ${(p) =>
    p.attachRight &&
    `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    
    .suggestions-stack {
      border-top-right-radius: 0;
    }
  `}
  
  ${(p) => `background: ${p.background};`}
`

const Input = styled.input<{ big: boolean; background: string }>`
  width: 100%;
  height: 100%;
  outline: none;
  border: none;

  ${(p) => `
    font-size: ${p.big ? 'var(--font-size--big)' : 'var(--font-size--base)'};
    background: ${p.background};
  `}
`

const SuggestionsStack = styled(Stack)<{ big: boolean } & StackProps>`
  position: absolute;

  ${(p) => `top: ${p.big ? '45px' : 'calc(var(--space--xl) - 2px)'};`}

  left: -1px;
  width: auto;
  min-width: calc(100% + 2px);
  cursor: default;
  border: 1px solid var(--colors-subtle);
  border-radius: var(--border-radius--xxs);
  background-color: var(--colors-foreground);
  max-height: 190px;
  overflow-y: scroll;
  pointer-events: all;
  z-index: var(--zindex-dropdown);
`

interface SuggestionDivProps {
  highlighted: boolean
  divider: boolean
}

const SuggestionDiv = styled.div<SuggestionDivProps>`
  display: flex;
  align-items: center;
  height: var(--space--xl);
  padding: 0 var(--space--s);
  cursor: pointer;

  ${(p) =>
    p.highlighted &&
    `
    background: var(--colors-highlight);
  `}

  ${(p) =>
    p.divider &&
    `
    border-top: 1px solid var(--colors-hairline);
  `}
`
